import React, { useState, useEffect } from 'react';
import { useApp } from '@/context/AppContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { errMsg } from '@/lib/api';
import { Loader2, Fingerprint, ShieldCheck } from 'lucide-react';
import { readCachedBranding, readCachedLogin, DEFAULT_LOGIN, buildBgStyle } from '@/lib/theme';
import {
  isMobile, isQuickLoginAvailable, getQuickHint, setQuickHint, quickLogin,
} from '@/lib/webauthn';
import ovrApi, { setOvrToken } from '@/lib/ownerOverride';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export default function Login() {
  const { login, loginWithToken, user } = useApp();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  // --- Mobile Quick Login (WebAuthn / passkey) ---
  // Only mobile devices with a platform authenticator (Face ID / Touch ID /
  // fingerprint) ever see Quick Login. Desktop keeps username + password.
  const mobile = isMobile();
  const [quickAvail, setQuickAvail] = useState(false);
  const [quickHint] = useState(getQuickHint());
  const [quickBusy, setQuickBusy] = useState(false);
  useEffect(() => { if (mobile) isQuickLoginAvailable().then(setQuickAvail); }, [mobile]);
  const showQuick = mobile && quickAvail;

  const doQuickLogin = async () => {
    if (quickBusy) return;
    setQuickBusy(true);
    try {
      const { token, user: u } = await quickLogin(quickHint?.username || '');
      const me = await loginWithToken(token, u);
      setQuickHint(me);
      toast.success('Welcome back to SHABZ CLOSET');
      navigate('/');
    } catch (err) {
      const name = err?.name;
      if (name === 'NotAllowedError' || name === 'AbortError') {
        toast.error('Quick Login was cancelled.');
      } else {
        toast.error(errMsg(err) || 'Quick Login failed. Use your password instead.');
      }
    } finally {
      setQuickBusy(false);
    }
  };

  // --- Owner Override (PIN-only admin access from any device) ---
  const [ownerOpen, setOwnerOpen] = useState(false);
  const [ownerUsername, setOwnerUsername] = useState('');
  const [ownerPin, setOwnerPin] = useState('');
  const [ownerBusy, setOwnerBusy] = useState(false);
  const verifyOwner = async () => {
    if (ownerBusy) return;
    if (!ownerUsername.trim() || !ownerPin.trim()) { toast.error('Enter the Owner username and Override PIN.'); return; }
    setOwnerBusy(true);
    try {
      const r = await ovrApi.post('/owner-override/verify', {
        owner_username: ownerUsername.trim(), pin: ownerPin.trim(),
      });
      setOvrToken(r.data.data.token);
      setOwnerOpen(false); setOwnerPin('');
      toast.success('Owner verified — entering Owner Mode.');
      navigate('/owner-override');
    } catch (err) {
      const code = err?.response?.status;
      if (code === 429) toast.error(errMsg(err));
      else toast.error('Owner verification failed.');
      setOwnerPin('');
    } finally {
      setOwnerBusy(false);
    }
  };
  const branding = readCachedBranding() || {};
  const logoSizeCls = { small: 'h-12', medium: 'h-16', large: 'h-20' }[branding.logoSize || 'medium'];
  const businessName = (branding.businessName || '').trim() || 'SHABZ CLOSET';
  const loginCfg = { ...DEFAULT_LOGIN, ...(readCachedLogin() || {}) };
  const coverStyle = loginCfg.bgType && loginCfg.bgType !== 'default' ? buildBgStyle(loginCfg, false) : null;

  const landingFor = (u) => {
    if (!u) return '/';
    if (u.role === 'PARTNER') return '/management';
    const perms = Array.isArray(u.permissions) ? u.permissions : [];
    const has = (p) => u.role === 'OWNER' || u.owner_access || perms.includes(p);
    // Dashboard-only user (no operational access) lands straight on the dashboard.
    if (has('dashboard') && !has('billing_view') && !has('stock_view') && !has('billing_create')) return '/management';
    return '/';
  };
  React.useEffect(() => { if (user) navigate(landingFor(user)); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return; // double-submit guard
    if (!username.trim() || !password) { toast.error('Enter your username and password.'); return; }
    setLoading(true);
    // Attempt the login (a POST — never touched by any service worker) directly,
    // retrying ONLY on transient failures (network error / 5xx) to ride out cold
    // starts. Definitive answers (401/403/other 4xx) are surfaced immediately.
    // NOTE: we intentionally do NOT gate on a GET /api/health pre-check, because a
    // stale/broken service worker can fail that GET and wrongly report the server
    // as "unavailable" even though sign-in itself works fine.
    const labels = ['Signing in…', 'Starting Shabz Closet…', 'Almost ready…', 'Server is taking longer than usual…'];
    const delays = [0, 1500, 3000, 4500];
    let lastErr = null;
    try {
      for (let i = 0; i < delays.length; i++) {
        if (delays[i]) await sleep(delays[i]);
        setStatusMsg(labels[Math.min(i, labels.length - 1)]);
        try {
          const me = await login(username.trim(), password);
          if (mobile) setQuickHint(me); // remember for next-time Quick Login
          toast.success('Welcome to SHABZ CLOSET');
          navigate('/');
          return;
        } catch (err) {
          const code = err?.response?.status;
          if (code === 401) { toast.error('Incorrect username or password.'); return; }
          if (code === 403) { toast.error(errMsg(err) || 'Your account has been disabled. Contact the Owner.'); return; }
          if (code && code < 500) { toast.error(errMsg(err)); return; }
          lastErr = err; // transient (no response / 5xx) → keep retrying
        }
      }
      // Exhausted retries on transient errors only.
      if (!lastErr?.response?.status) toast.error('Connection lost. Please check your internet and try again.');
      else toast.error('Shabz Closet server is temporarily unavailable. Please try again in a moment.');
    } finally {
      setLoading(false);
      setStatusMsg('');
    }
  };

  return (
    <div className={`${coverStyle ? '' : 'app-bg'} relative flex min-h-screen flex-col items-center justify-center px-5`}
      style={{ ...(coverStyle || {}), paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
      {coverStyle && loginCfg.overlay && (
        <div className="absolute inset-0" style={{ backgroundColor: '#000', opacity: loginCfg.overlayOpacity }} />
      )}
      <div className="relative w-full max-w-sm">
        <div className="mb-8 text-center">
          {branding.logo ? (
            <img src={branding.logo} alt="Company logo" data-testid="login-logo"
              className={`mx-auto mb-4 ${logoSizeCls} w-auto max-w-[70%] object-contain`} />
          ) : (
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-sm" data-testid="login-logo-placeholder">
              <span className="font-display text-2xl font-bold leading-none">{(businessName || 'S').charAt(0).toUpperCase()}</span>
            </div>
          )}
          <h1 className="font-display text-3xl font-bold tracking-tight">{businessName}</h1>
          <p className="mt-1 text-sm text-muted-foreground">Billing &amp; Store Operations</p>
        </div>
        <form onSubmit={submit} className="rounded-2xl border border-border bg-card p-5 shadow-sm">
          {showQuick && (
            <div className="mb-5" data-testid="quick-login-block">
              <button type="button" onClick={doQuickLogin} disabled={quickBusy}
                data-testid="quick-login-btn"
                className="flex w-full flex-col items-center gap-3 rounded-2xl border border-primary/30 bg-primary/5 p-5 transition-colors hover:bg-primary/10 active:bg-primary/15 disabled:opacity-70">
                <span className="sc-fp-wrap h-16 w-16 rounded-full bg-primary/10">
                  {quickBusy && <span className="sc-fp-ring" />}
                  <Fingerprint className={`sc-fp-icon h-9 w-9 text-primary ${quickBusy ? 'sc-fp-busy' : ''}`} />
                </span>
                <span className="text-center">
                  <span className="block text-sm font-semibold text-foreground">
                    {quickBusy ? 'Verifying…' : 'Quick Login'}
                  </span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    {quickHint?.name
                      ? `Sign in as ${quickHint.name} with Face ID / fingerprint`
                      : 'Sign in with Face ID / fingerprint'}
                  </span>
                </span>
              </button>
              <div className="my-4 flex items-center gap-3">
                <span className="h-px flex-1 bg-border" />
                <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">or use password</span>
                <span className="h-px flex-1 bg-border" />
              </div>
            </div>
          )}
          <div className="space-y-4">
            <div>
              <Label htmlFor="username">Username</Label>
              <Input id="username" data-testid="login-username" value={username}
                onChange={(e) => setUsername(e.target.value)} className="mt-1 h-11 rounded-xl"
                placeholder="owner" autoCapitalize="none" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="login-password" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} className="mt-1 h-11 rounded-xl"
                placeholder="••••••" />
            </div>
            <Button type="submit" data-testid="login-submit" disabled={loading}
              className="h-12 w-full rounded-xl bg-primary text-primary-foreground text-base font-semibold hover:bg-primary/90">
              {loading ? <span className="flex items-center gap-2"><Loader2 className="h-5 w-5 animate-spin" />{statusMsg || 'Please wait…'}</span> : 'Sign in'}
            </Button>
            {loading && statusMsg && (
              <p className="text-center text-xs text-muted-foreground" data-testid="login-status">{statusMsg}</p>
            )}
          </div>
        </form>
        <div className="mt-5 text-center">
          <button type="button" onClick={() => setOwnerOpen(true)} data-testid="owner-access-link"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline">
            <ShieldCheck className="h-3.5 w-3.5" />Owner / Admin Access
          </button>
        </div>
      </div>

      <Dialog open={ownerOpen} onOpenChange={(o) => { if (!o) { setOwnerOpen(false); setOwnerPin(''); } }}>
        <DialogContent className="max-w-sm" data-testid="owner-verify-dialog">
          <DialogHeader>
            <DialogTitle>Owner verification</DialogTitle>
            <DialogDescription>Verify yourself to manage users on this device. This does not sign you in as an employee.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div>
              <Label htmlFor="ovr-username">Owner username</Label>
              <Input id="ovr-username" value={ownerUsername} onChange={(e) => setOwnerUsername(e.target.value)}
                className="mt-1 h-11 rounded-xl" autoCapitalize="none" placeholder="owner" data-testid="owner-verify-username" />
            </div>
            <div>
              <Label htmlFor="ovr-pin">Owner Override PIN</Label>
              <Input id="ovr-pin" type="password" inputMode="numeric" value={ownerPin}
                onChange={(e) => setOwnerPin(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') verifyOwner(); }}
                className="mt-1 h-11 rounded-xl tracking-widest" placeholder="••••" data-testid="owner-verify-pin" />
              <p className="mt-1.5 text-[11px] text-muted-foreground">Set your PIN in Settings → Security while signed in.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" className="h-11 rounded-xl" onClick={() => { setOwnerOpen(false); setOwnerPin(''); }} disabled={ownerBusy}>Cancel</Button>
            <Button className="h-11 rounded-xl" onClick={verifyOwner} disabled={ownerBusy} data-testid="owner-verify-submit">
              {ownerBusy ? <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Verifying…</span> : 'Verify Owner'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
